import React from 'react';
import { Box, Card, CardContent, Typography, Grid, Button, Chip, useTheme } from '@mui/material';
import { PlayArrow as PlayArrowIcon, CheckCircle as CheckCircleIcon, Block as BlockIcon } from '@mui/icons-material';

const plans = [
  {
    name: 'Free',
    price: 'Miễn phí',
    priceDetail: '0₫',
    benefits: [
      { text: 'Học các bài học cơ bản', icon: <CheckCircleIcon color="success" fontSize="small" /> },
      { text: 'Không được học các khoá học video', icon: <BlockIcon color="error" fontSize="small" /> },
    ],
    highlight: false,
  },
  {
    name: 'Monthly',
    price: '$4 / tháng',
    priceDetail: '≈ 100,000₫',
    benefits: [
      { text: 'Học các bài học cơ bản', icon: <CheckCircleIcon color="success" fontSize="small" /> },
      { text: 'Được học các khoá học video', icon: <CheckCircleIcon color="success" fontSize="small" /> },
    ],
    highlight: true,
  },
  {
    name: 'Yearly',
    price: '$45 / năm',
    priceDetail: '≈ 1,100,000₫',
    benefits: [
      { text: 'Học các bài học cơ bản', icon: <CheckCircleIcon color="success" fontSize="small" /> },
      { text: 'Được học các khoá học video', icon: <CheckCircleIcon color="success" fontSize="small" /> },
    ],
    highlight: false,
  },
];

const Pricing: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        py: { xs: 3, md: 6 },
        px: { xs: 1, md: 2 },
        minHeight: '80vh',
        position: 'relative',
        background: isDark
          ? 'linear-gradient(135deg, #181c24 0%, #23263a 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #e9ecef 100%)',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isDark
            ? 'radial-gradient(circle at 80% 20%, rgba(102,126,234,0.08) 0%, transparent 70%)'
            : 'radial-gradient(circle at 80% 20%, rgba(102,126,234,0.08) 0%, transparent 70%)',
          zIndex: 0,
        },
      }}
    >
      <Typography
        variant="h3"
        align="center"
        fontWeight={800}
        mb={2}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        Membership Pricing
      </Typography>
      <Typography
        variant="subtitle1"
        align="center"
        color={isDark ? 'grey.300' : 'text.secondary'}
        mb={6}
        sx={{ fontWeight: 500 }}
      >
        Chọn gói thành viên phù hợp để trải nghiệm đầy đủ các tính năng học tiếng Lào!
      </Typography>
      <Grid container spacing={4} justifyContent="center" sx={{ position: 'relative', zIndex: 1 }}>
        {plans.map((plan, idx) => (
          <Grid item xs={12} sm={6} md={4} key={plan.name}>
            <Card
              sx={{
                borderRadius: 5,
                boxShadow: plan.highlight
                  ? '0 8px 32px rgba(102,126,234,0.25)'
                  : isDark
                  ? '0 4px 16px rgba(0,0,0,0.25)'
                  : '0 4px 16px rgba(102,126,234,0.08)',
                border: plan.highlight
                  ? '2.5px solid transparent'
                  : `1.5px solid ${isDark ? '#333' : '#e0e0e0'}`,
                background: plan.highlight
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%) padding-box, linear-gradient(135deg, #f8fafc 0%, #e9ecef 100%) border-box'
                  : isDark
                  ? 'rgba(255,255,255,0.03)'
                  : 'rgba(255,255,255,0.97)',
                color: plan.highlight ? 'white' : isDark ? 'grey.100' : 'inherit',
                position: 'relative',
                minHeight: 370,
                transition: 'transform 0.2s cubic-bezier(.4,2,.6,1), box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-6px) scale(1.03)',
                  boxShadow: plan.highlight
                    ? '0 16px 48px 0 rgba(102,126,234,0.35)'
                    : isDark
                    ? '0 8px 32px rgba(0,0,0,0.32)'
                    : '0 8px 32px rgba(102,126,234,0.13)',
                },
                overflow: 'visible',
              }}
            >
              {plan.highlight && (
                <Chip
                  label="Khuyên dùng"
                  color="secondary"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 18,
                    right: 18,
                    fontWeight: 700,
                    zIndex: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                  }}
                />
              )}
              <CardContent sx={{ px: 3, py: 4 }}>
                <Typography
                  variant="h5"
                  fontWeight={800}
                  mb={1}
                  align="center"
                  sx={{ letterSpacing: 1 }}
                >
                  {plan.name}
                </Typography>
                <Typography
                  variant="h4"
                  fontWeight={900}
                  align="center"
                  mb={1}
                  sx={{
                    background: plan.highlight
                      ? 'linear-gradient(135deg, #fff 0%, #e0e0e0 100%)'
                      : 'none',
                    backgroundClip: plan.highlight ? 'text' : undefined,
                    WebkitBackgroundClip: plan.highlight ? 'text' : undefined,
                    WebkitTextFillColor: plan.highlight ? 'transparent' : undefined,
                  }}
                >
                  {plan.price}
                </Typography>
                <Typography
                  variant="body2"
                  align="center"
                  mb={2}
                  sx={{ color: plan.highlight ? 'rgba(255,255,255,0.85)' : isDark ? 'grey.400' : 'text.secondary' }}
                >
                  {plan.priceDetail}
                </Typography>
                <Box mb={3}>
                  {plan.benefits.map((benefit, i) => (
                    <Box key={i} display="flex" alignItems="center" mb={1.2}>
                      {benefit.icon}
                      <Typography
                        variant="body1"
                        ml={1}
                        sx={{ color: plan.highlight ? 'white' : isDark ? 'grey.200' : 'text.primary', fontWeight: 500 }}
                      >
                        {benefit.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Button
                  variant={plan.highlight ? 'contained' : 'outlined'}
                  color={plan.highlight ? 'secondary' : 'primary'}
                  fullWidth
                  size="large"
                  endIcon={<PlayArrowIcon />}
                  disabled={plan.name === 'Free'}
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    py: 1.2,
                    borderRadius: 3,
                    background: plan.highlight ? 'linear-gradient(135deg, #ffb347 0%, #ffcc80 100%)' : undefined,
                    color: plan.highlight ? '#4b2e05' : undefined,
                    boxShadow: plan.highlight ? '0 2px 8px rgba(255,179,71,0.15)' : undefined,
                    '&:hover': {
                      background: plan.highlight
                        ? 'linear-gradient(135deg, #ffcc80 0%, #ffb347 100%)'
                        : undefined,
                    },
                  }}
                >
                  {plan.name === 'Free' ? 'Đang sử dụng' : 'Chọn gói này'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Pricing; 